import React, { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Upload,
  FileArchive,
  Loader2,
  CheckCircle2,
  AlertCircle,
  X,
  FolderUp,
  ShieldCheck,
  Package,
  KeyRound,
  Scale,
  Server,
  Container,
  Link as LinkIcon,
} from "lucide-react";
import Layout, { PageHeader, PageContainer } from "../components/ui/Layout";
import { API_BASE_URL } from "../api/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/shadcn/card";
import { Button } from "../components/shadcn/button";
import { Input } from "../components/shadcn/input";
import { Progress } from "../components/shadcn/progress";
import { cn } from "../lib/utils";

interface UploadResponse {
  repo_id: string;
  job_id: string;
  message: string;
}

interface UsageLimits {
  scansUsed: number;
  maxScans: number | null;
  reposUsed: number;
  maxRepos: number | null;
}

/** The org's plan and its published caps, from /v1/usage. */
interface PlanUsage {
  id: string;
  displayName: string;
  maxRepos: number;
  maxScansPerMonth: number;
  maxUploadBytes: number;
  reposUsed: number;
  scansThisMonth: number;
}

const SCANNERS = [
  { name: "SAST", description: "Code vulnerabilities", icon: ShieldCheck },
  { name: "SCA", description: "Dependency CVEs", icon: Package },
  { name: "Secrets", description: "Secrets & API keys", icon: KeyRound },
  { name: "License", description: "License compliance", icon: Scale },
  { name: "IaC", description: "IaC misconfigurations", icon: Server },
  { name: "Container", description: "Container security", icon: Container },
];

/** Matches the API's multipart limit; the interface must not offer more. */
const MAX_UPLOAD_BYTES = 500 * 1024 * 1024;
const MAX_UPLOAD_LABEL = "500 MB";

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
  return `${Math.round(bytes / 1024 / 1024)} MB`;
}

/**
 * Record the just-started scan under the key RepoDetails reads, so the repo
 * page shows live progress the moment we land on it. RepoDetails also falls
 * back to GET /v1/repos/:id/active-scan, which covers a different device or
 * cleared storage; this makes the common path instant.
 */
function handOffScanToRepoPage(repoId: string, jobId?: string) {
  if (!repoId || !jobId) return;
  try {
    localStorage.setItem(
      `paxley_scan_${repoId}`,
      JSON.stringify({ jobId, startedAt: Date.now() })
    );
  } catch {
    /* storage unavailable — the server-side fallback still covers it */
  }
}

export default function UploadCode() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"url" | "zip">("url");
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [projectName, setProjectName] = useState("");
  const [gitUrl, setGitUrl] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /** Mirrors the server rule: cap at 100 chars, then non-alphanumerics to "-". */
  const normalizedName = projectName
    .trim()
    .slice(0, 100)
    .replace(/[^a-zA-Z0-9-_]/g, "-");

  // Plan allowances (present only for metered orgs, e.g. trials).
  const usageQuery = useQuery({
    queryKey: ["usage-limits"],
    queryFn: async (): Promise<{ limits: UsageLimits | null; plan: PlanUsage | null }> => {
      const response = await fetch(`${API_BASE_URL || ""}/v1/usage`, {
        credentials: "include",
      });
      if (!response.ok) return { limits: null, plan: null };
      const body = await response.json();
      return { limits: body?.limits ?? null, plan: body?.plan ?? null };
    },
    staleTime: 15000,
  });
  const usage = usageQuery.data?.limits ?? null;
  const planUsage = usageQuery.data?.plan ?? null;
  // The archive size this org may upload: its plan's cap, never above the
  // platform cap. Until the plan is known, the platform cap is shown.
  const uploadCapBytes =
    planUsage && planUsage.maxUploadBytes !== -1
      ? Math.min(planUsage.maxUploadBytes, MAX_UPLOAD_BYTES)
      : MAX_UPLOAD_BYTES;
  const uploadCapLabel = planUsage ? formatBytes(uploadCapBytes) : MAX_UPLOAD_LABEL;

  const urlMutation = useMutation({
    mutationFn: async (payload: { gitUrl: string; projectName: string }) => {
      const response = await fetch(`${API_BASE_URL || ""}/v1/scan-url`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });
      if (!response.ok) {
        let errData: any = {};
        try {
          errData = await response.json();
        } catch {
          errData = { error: response.statusText };
        }
        throw new Error(errData.message || "Could not start the scan");
      }
      return response.json() as Promise<UploadResponse>;
    },
    onSuccess: (data) => {
      handOffScanToRepoPage(data.repo_id, data.job_id);
      setTimeout(() => navigate(`/dashboard/repos/${data.repo_id}`), 1500);
    },
  });

  // Uploaded with XMLHttpRequest rather than fetch so we can show real upload
  // progress and, critically, surface a specific reason on failure. The old
  // fetch had no progress and collapsed every error — including a connection
  // dropped mid-transfer on a large archive — into a bare "Upload failed",
  // which is exactly what a client hit and could not act on.
  const uploadMutation = useMutation({
    mutationFn: (formData: FormData) =>
      new Promise<UploadResponse>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API_BASE_URL || ""}/v1/upload`);
        xhr.withCredentials = true;

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText) as UploadResponse);
            } catch {
              reject(new Error("The upload completed but the response could not be read. Please try again."));
            }
            return;
          }
          let serverMsg = "";
          try {
            serverMsg = JSON.parse(xhr.responseText)?.message || "";
          } catch {
            /* non-JSON error body */
          }
          if (xhr.status === 413) {
            // Only claim the archive is over the limit when the server actually
            // said so. A 413 with no JSON body comes from a proxy hop, and
            // asserting a cause we haven't verified produces nonsense like
            // telling someone a 17 MB file exceeds a 500 MB limit.
            reject(new Error(
              serverMsg ||
                `The server rejected this upload as too large (HTTP 413). The limit for archives on your plan is ${uploadCapLabel} — if this file is well under that, please report it to us. You can also scan the repository from its git URL instead.`
            ));
          } else {
            reject(new Error(
              serverMsg ||
                `Upload failed (HTTP ${xhr.status}). You can also scan this repository directly from its git URL instead.`
            ));
          }
        };

        // Network-level failure — the common case for a large upload over a
        // slower connection is the transfer being cut off partway.
        xhr.onerror = () =>
          reject(new Error(
            "The upload was interrupted before it finished — usually the connection dropped mid-transfer. Please retry, or scan the repository from its git URL instead (no upload needed)."
          ));
        xhr.onabort = () => reject(new Error("Upload cancelled."));

        setUploadProgress(0);
        xhr.send(formData);
      }),
    onSuccess: (data) => {
      // Hand the running scan to the repo page. Without this the repo page has
      // no job id and renders the empty "No findings yet — run a scan" state
      // while the scan is actually running, which looks like nothing happened.
      handOffScanToRepoPage(data.repo_id, data.job_id);
      setTimeout(() => {
        navigate(`/dashboard/repos/${data.repo_id}`);
      }, 2000);
    },
  });

  /**
   * Accept a file, or say why not.
   *
   * Both the drop target and the file picker used to discard an unsuitable
   * file in silence, so a wrong format or an oversized archive looked like
   * the page had simply ignored the action.
   */
  const acceptFile = useCallback(
    (candidate: File | undefined | null) => {
      if (!candidate) return;
      setFileError(null);

      const isArchive =
        candidate.name.endsWith(".zip") || candidate.name.endsWith(".tar.gz");
      if (!isArchive) {
        setFile(null);
        setFileError(
          `${candidate.name} is not a supported archive. Upload a .zip or .tar.gz, or scan the repository from a git URL instead.`
        );
        return;
      }
      if (candidate.size > uploadCapBytes) {
        setFile(null);
        setFileError(
          `${candidate.name} is ${formatBytes(candidate.size)}, over the ${uploadCapLabel} upload limit of your plan. Scanning from a git URL has no size limit, or you can archive the source without its .git history.`
        );
        return;
      }

      setFile(candidate);
      if (!projectName) {
        setProjectName(candidate.name.replace(/\.(zip|tar\.gz)$/, ""));
      }
    },
    [projectName]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);

      acceptFile(e.dataTransfer.files[0]);
    },
    [acceptFile]
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    acceptFile(e.target.files?.[0]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !projectName.trim()) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("projectName", projectName.trim());

    uploadMutation.mutate(formData);
  };

  const handleUrlSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!gitUrl.trim()) return;
    urlMutation.mutate({ gitUrl: gitUrl.trim(), projectName: projectName.trim() });
  };

  const urlBusy = urlMutation.isPending || urlMutation.isSuccess;

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isUploading = uploadMutation.isPending;
  const isSuccess = uploadMutation.isSuccess;
  const isError = uploadMutation.isError;

  return (
    <Layout>
      <PageContainer>
        <PageHeader
          title="Upload Code"
          description="Scan your codebase without GitHub integration"
        />

        <div className="max-w-2xl mx-auto space-y-6">
          {/* Info Card */}
          <Card className="bg-brand/5 border-brand/20">
            <CardContent className="flex gap-3 p-4">
              <div className="h-10 w-10 rounded-xl bg-brand/15 text-brand flex items-center justify-center flex-shrink-0">
                <FolderUp className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-base font-medium text-text">
                  Scan without GitHub
                </CardTitle>
                <CardDescription className="mt-1">
                  Point Paxley at a public repository URL, or upload a ZIP or
                  TAR.GZ archive of your codebase. Either way, we run the full
                  analysis — code security, dependencies, secrets, licenses,
                  and infrastructure — no GitHub connection required.
                </CardDescription>
              </div>
            </CardContent>
          </Card>

          {/* Plan caps (Community, Starter, Growth) */}
          {planUsage && (planUsage.maxRepos !== -1 || planUsage.maxScansPerMonth !== -1) && (
            <Card className="border-border/60">
              <CardContent className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-text2">
                  <ShieldCheck className="h-4 w-4 text-brand" aria-hidden="true" />
                  <span className="font-medium">{planUsage.displayName} plan</span>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  {planUsage.maxScansPerMonth !== -1 && (
                    <span
                      className={cn(
                        "tabular-nums",
                        planUsage.scansThisMonth >= planUsage.maxScansPerMonth ? "font-semibold text-critical" : "text-muted"
                      )}
                    >
                      {planUsage.scansThisMonth} of {planUsage.maxScansPerMonth} scans this month
                    </span>
                  )}
                  {planUsage.maxRepos !== -1 && (
                    <span
                      className={cn(
                        "tabular-nums",
                        planUsage.reposUsed >= planUsage.maxRepos ? "font-semibold text-critical" : "text-muted"
                      )}
                    >
                      {planUsage.reposUsed} of {planUsage.maxRepos} repositories
                    </span>
                  )}
                  <span className="text-muted">{uploadCapLabel} per upload</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Trial allowance (metered orgs only) */}
          {usage && (usage.maxScans !== null || usage.maxRepos !== null) && (
            <Card className="border-border/60">
              <CardContent className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-text2">
                  <ShieldCheck className="h-4 w-4 text-brand" aria-hidden="true" />
                  <span className="font-medium">Trial allowance</span>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  {usage.maxScans !== null && (
                    <span
                      className={cn(
                        "tabular-nums",
                        usage.scansUsed >= usage.maxScans ? "font-semibold text-critical" : "text-muted"
                      )}
                    >
                      {usage.scansUsed} of {usage.maxScans} scans used
                    </span>
                  )}
                  {usage.maxRepos !== null && (
                    <span
                      className={cn(
                        "tabular-nums",
                        usage.reposUsed >= usage.maxRepos ? "font-semibold text-critical" : "text-muted"
                      )}
                    >
                      {usage.reposUsed} of {usage.maxRepos} repositories
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Mode toggle */}
          <div className="grid grid-cols-2 gap-2 rounded-xl border border-border/70 bg-surface2/40 p-1">
            <button
              type="button"
              onClick={() => setMode("url")}
              className={cn(
                "flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition-colors",
                mode === "url" ? "bg-brand text-white shadow-sm" : "text-text2 hover:text-text"
              )}
            >
              <LinkIcon className="h-4 w-4" />
              From Git URL
            </button>
            <button
              type="button"
              onClick={() => setMode("zip")}
              className={cn(
                "flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition-colors",
                mode === "zip" ? "bg-brand text-white shadow-sm" : "text-text2 hover:text-text"
              )}
            >
              <FileArchive className="h-4 w-4" />
              Upload a ZIP
            </button>
          </div>

          {/* Git URL form */}
          {mode === "url" && (
            <form onSubmit={handleUrlSubmit} className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="gitUrl" className="block text-sm font-medium text-text">
                  Repository URL
                </label>
                <Input
                  type="url"
                  id="gitUrl"
                  value={gitUrl}
                  onChange={(e) => setGitUrl(e.target.value)}
                  placeholder="https://github.com/owner/repository"
                  disabled={urlBusy}
                  required
                />
                <p className="text-xs text-muted">
                  Any public git repository. We clone it read-only, scan it, and
                  clean up — nothing is stored except the results.
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="projectNameUrl" className="block text-sm font-medium text-text">
                  Project name <span className="text-muted font-normal">(optional)</span>
                </label>
                <Input
                  type="text"
                  id="projectNameUrl"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="Leave blank to use the repository name"
                  disabled={urlBusy}
                />
              </div>

              {urlMutation.isSuccess && (
                <Card className="p-4 bg-success/10 border-success/20">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-success" />
                    <div>
                      <div className="text-sm font-medium text-success">Scan started</div>
                      <div className="text-xs text-muted">Cloning the repository and running the analysis. Taking you to the results…</div>
                    </div>
                  </div>
                </Card>
              )}
              {urlMutation.isError && (
                <Card className="p-4 bg-critical/10 border-critical/20">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-critical" />
                    <div>
                      <div className="text-sm font-medium text-critical">Could not start the scan</div>
                      <div className="text-xs text-muted">{urlMutation.error?.message || "Please check the URL and try again"}</div>
                    </div>
                  </div>
                </Card>
              )}

              <Button
                type="submit"
                className="w-full h-12 text-base gap-2"
                disabled={!gitUrl.trim() || urlBusy}
              >
                {urlMutation.isPending ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Starting scan…
                  </>
                ) : urlMutation.isSuccess ? (
                  <>
                    <CheckCircle2 className="h-5 w-5" />
                    Scan started
                  </>
                ) : (
                  <>
                    <ShieldCheck className="h-5 w-5" />
                    Clone &amp; Scan
                  </>
                )}
              </Button>
            </form>
          )}

          {mode === "zip" && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Project Name */}
            <div className="space-y-2">
              <label
                htmlFor="projectName"
                className="block text-sm font-medium text-text"
              >
                Project Name
              </label>
              <Input
                type="text"
                id="projectName"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="my-awesome-project"
                disabled={isUploading || isSuccess}
                required
              />
              {normalizedName && normalizedName !== projectName.trim() ? (
                // The server normalises the name (and caps it at 100 chars).
                // Showing the result up front stops people being surprised by a
                // repository that is not called what they typed.
                <p className="text-xs text-muted">
                  Will be saved as{" "}
                  <span className="font-mono text-text">{normalizedName}</span>
                  {projectName.trim().length > 100 && " (shortened)"}
                </p>
              ) : (
                <p className="text-xs text-muted">
                  This name will be used to identify your project in the dashboard
                </p>
              )}
            </div>

            {/* File Drop Zone */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-text">
                Code Archive
              </label>
              <Card
                onDragOver={(e: React.DragEvent) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() =>
                  !isUploading && !isSuccess && fileInputRef.current?.click()
                }
                className={cn(
                  "border-2 border-dashed p-8 text-center transition-colors cursor-pointer",
                  dragOver
                    ? "border-brand bg-brand/5"
                    : file
                      ? "border-success/50 bg-success/5"
                      : "border-border hover:border-muted",
                  (isUploading || isSuccess) &&
                    "pointer-events-none opacity-60"
                )}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".zip,.tar.gz"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={isUploading || isSuccess}
                />

                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="h-12 w-12 rounded-xl bg-success/15 text-success flex items-center justify-center">
                      <FileArchive className="h-6 w-6" />
                    </div>
                    <div className="text-left">
                      <div className="font-medium text-text">{file.name}</div>
                      <div className="text-sm text-muted">
                        {formatFileSize(file.size)}
                      </div>
                    </div>
                    {!isUploading && !isSuccess && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="ml-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFile(null);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Upload className="h-10 w-10 text-muted mx-auto" />
                    <div className="text-text font-medium">
                      Drop your code archive here
                    </div>
                    <div className="text-sm text-muted">
                      or click to browse
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                    >
                      Choose File
                    </Button>
                    <div className="text-xs text-muted">
                      ZIP or TAR.GZ up to {uploadCapLabel}
                    </div>
                  </div>
                )}
              </Card>
            </div>

            {/* Upload Progress / Status */}
            {isUploading && (
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <Loader2 className="h-5 w-5 animate-spin text-brand" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-text">
                      {uploadProgress < 100
                        ? `Uploading… ${uploadProgress}%`
                        : "Upload complete — starting scan…"}
                    </div>
                    <div className="text-xs text-muted">
                      {uploadProgress < 100
                        ? "Large archives can take a few minutes to upload — keep this tab open."
                        : "Queuing the scan. This can take several minutes for large codebases."}
                    </div>
                  </div>
                </div>
                <Progress value={uploadProgress} className="mt-3 h-2" />
              </Card>
            )}

            {isSuccess && (
              <Card className="p-4 bg-success/10 border-success/20">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-success" />
                  <div>
                    <div className="text-sm font-medium text-success">
                      Upload successful!
                    </div>
                    <div className="text-xs text-muted">
                      Redirecting to scan results...
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {fileError && (
              <Card className="p-4 bg-critical/10 border-critical/20">
                <div className="flex items-start gap-3">
                  <AlertCircle className="mt-0.5 h-5 w-5 flex-none text-critical" />
                  <div className="text-sm leading-6 text-critical">{fileError}</div>
                </div>
              </Card>
            )}

            {isError && (
              <Card className="p-4 bg-critical/10 border-critical/20">
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-5 w-5 text-critical" />
                  <div>
                    <div className="text-sm font-medium text-critical">
                      Upload failed
                    </div>
                    <div className="text-xs text-muted">
                      {uploadMutation.error?.message || "Please try again"}
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full h-12 text-base gap-2"
              disabled={!file || !projectName.trim() || isUploading || isSuccess}
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Scanning...
                </>
              ) : isSuccess ? (
                <>
                  <CheckCircle2 className="h-5 w-5" />
                  Complete!
                </>
              ) : (
                <>
                  <Upload className="h-5 w-5" />
                  Upload & Scan
                </>
              )}
            </Button>
          </form>
          )}

          {/* Supported Scanners */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">
                What we scan for:
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {SCANNERS.map((scanner) => (
                  <Card
                    key={scanner.name}
                    className="flex items-center gap-2.5 p-3 shadow-none"
                  >
                    <scanner.icon className="h-4 w-4 text-muted flex-shrink-0" />
                    <span className="text-sm text-muted">
                      {scanner.description}
                    </span>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </PageContainer>
    </Layout>
  );
}
